import { BUTTON_LABELS } from '@/constants/labels';
import { roleplayCardHistoryItemStyles } from '../../../RoleplayCard.styles';
import { ShareWithManagerButton } from '@/components/Buttons';
import { SkeletonLoader } from '@/components/Loaders';
import React from 'react';

/**
 * RoleplayCardHistoryItemActions renders the action buttons for a roleplay
 * card history item, including the "Share with Manager" and "View Scorecard"
 * buttons. It handles the loading state and conditional rendering based on the
 * simulation type and whether the score has already been shared.
 */
export const RoleplayCardHistoryItemActions: React.FC<{
  isLoading?: boolean;
  onShareWithManager?: () => void;
  onViewScoreCard?: () => void;
  scoreCardId?: number;
  sessionId?: number;
  sharedWithManager?: boolean;
  simulationType?: string;
  userId?: string;
}> = ({
  isLoading,
  onShareWithManager,
  onViewScoreCard,
  scoreCardId,
  sessionId,
  sharedWithManager,
  simulationType,
  userId,
}) => {
  // ============================
  // Subcomponents
  // ============================

  /**
   * ShareWithManagerBtn renders the "Share with Manager" button, which is
   * conditionally displayed based on the simulation type and loading state. It
   * is disabled if the score has already been shared or if data is still loading.
   */
  const ShareWithManagerButtonProvider = () => {
    if (simulationType !== 'role-play' || isLoading) {
      return null;
    }

    return (
      <ShareWithManagerButton
        disabled={isLoading || sharedWithManager}
        onShare={onShareWithManager}
        initialState={sharedWithManager === true ? 'shared' : 'initial'}
        scoreCardId={scoreCardId}
        sessionId={sessionId}
        userId={userId}
      />
    );
  };

  /**
   * ViewScorecardButton renders the "View Scorecard" button, which is displayed
   * unless the data is still loading. When loading, it shows a skeleton loader instead.
   */
  const ViewScorecardButton = () => {
    return isLoading ? (
      <SkeletonLoader height="md" rounded="none" width="md" />
    ) : (
      <button
        type="button"
        className={roleplayCardHistoryItemStyles.sessionButton}
        disabled={isLoading}
        onClick={onViewScoreCard}
      >
        {BUTTON_LABELS.VIEW_SCORECARD}
      </button>
    );
  };

  // ============================
  // Render
  // ============================
  return (
    <React.Fragment>
      <ShareWithManagerButtonProvider />
      <ViewScorecardButton />
    </React.Fragment>
  );
};
